import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError, getToken } from "../../lib/api";
import { toast } from "sonner";

/**
 * Voice input hook. Prefers the browser's Web Speech API when available; falls
 * back to server-side Whisper transcription via /api/assistant/voice/transcribe.
 * Exposes: recording, mode ("web"|"whisper"|null), start(), stop(), supported.
 */
export function useVoiceInput({ onTranscript, lang = "en-US" }) {
  const [recording, setRecording] = useState(false);
  const [mode, setMode] = useState(null);
  const recRef = useRef(null);          // SpeechRecognition or MediaRecorder
  const chunksRef = useRef([]);
  const stopHandlerRef = useRef(null);

  const supported =
    typeof window !== "undefined" &&
    (Boolean(window.SpeechRecognition || window.webkitSpeechRecognition) ||
      Boolean(navigator?.mediaDevices?.getUserMedia && window.MediaRecorder));

  // Web Speech API path
  const startWebSpeech = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return false;
    try {
      const r = new SR();
      r.lang = lang;
      r.interimResults = false;
      r.maxAlternatives = 1;
      r.onresult = (ev) => {
        const t = ev.results?.[0]?.[0]?.transcript?.trim();
        if (t) onTranscript?.(t);
      };
      r.onerror = (ev) => {
        if (ev.error && ev.error !== "no-speech" && ev.error !== "aborted") {
          toast.error(`Voice error: ${ev.error}`);
        }
      };
      r.onend = () => {
        setRecording(false);
        setMode(null);
        recRef.current = null;
      };
      r.start();
      recRef.current = r;
      setMode("web");
      setRecording(true);
      return true;
    } catch (e) {
      return false;
    }
  }, [lang, onTranscript]);

  // Whisper fallback via MediaRecorder → backend
  const startWhisper = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      rec.ondataavailable = (ev) => ev.data && ev.data.size > 0 && chunksRef.current.push(ev.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime });
        chunksRef.current = [];
        if (blob.size < 800) {
          setRecording(false);
          setMode(null);
          return;
        }
        try {
          const fd = new FormData();
          fd.append("file", blob, "voice.webm");
          const backend = process.env.REACT_APP_BACKEND_URL;
          const token = getToken();
          const r = await fetch(`${backend}/api/assistant/voice/transcribe`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: fd,
          });
          if (!r.ok) throw new Error(await r.text());
          const j = await r.json();
          if (j.text) onTranscript?.(j.text);
          else toast.info("I couldn't hear anything.");
        } catch (e) {
          toast.error(formatApiError(e) || "Voice transcription failed");
        } finally {
          setRecording(false);
          setMode(null);
        }
      };
      rec.start();
      recRef.current = rec;
      setMode("whisper");
      setRecording(true);
      return true;
    } catch (e) {
      toast.error("Microphone access denied");
      return false;
    }
  }, [onTranscript]);

  const start = useCallback(async () => {
    if (recording) return;
    if (!startWebSpeech()) await startWhisper();
  }, [recording, startWebSpeech, startWhisper]);

  const stop = useCallback(() => {
    const r = recRef.current;
    if (!r) return;
    try {
      if (typeof r.stop === "function") r.stop();
    } catch {}
  }, []);

  useEffect(() => () => stop(), [stop]);
  stopHandlerRef.current = stop;

  return { recording, mode, start, stop, supported };
}
